import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { finalize } from 'rxjs';

import {
  CompanyContactRecord,
  CompanyContactResponse
} from '../models/company-contact';
import { CompanyServiceDetailsResponse } from '../models/company-service-details';
import { CollectDataService } from '../services/collect-data.service';
import { UiStateService } from '../services/ui-state.service';

@Component({
  selector: 'app-contact-detail',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './contact-detail.component.html',
  styleUrl: './contact-detail.component.css'
})
export class ContactDetailComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly collectDataService = inject(CollectDataService);
  private readonly uiState = inject(UiStateService);

  protected readonly serviceId = signal<number | null>(null);
  protected readonly locationId = signal<number | null>(null);
  protected readonly contactId = signal<number | null>(null);
  protected readonly record = signal<CompanyContactRecord | null>(null);
  protected readonly loading = signal<boolean>(true);
  protected readonly errorMessage = signal<string>('');
  protected readonly statusMessage = signal<string>('');
  protected readonly serviceLoading = signal<boolean>(false);
  protected readonly serviceError = signal<string>('');
  protected readonly serviceDetails =
    signal<CompanyServiceDetailsResponse | null>(null);
  protected readonly linkedinLoading = signal<boolean>(false);
  protected readonly linkedinAvailable = signal<boolean>(false);
  protected readonly returnTo = signal<'contacts' | 'company'>('contacts');

  constructor() {
    this.route.paramMap.subscribe((params) => {
      const serviceParam = params.get('serviceId');
      const locationParam = params.get('locationId');
      const contactParam = params.get('contactId');

      const parsedServiceId = serviceParam ? Number(serviceParam) : NaN;
      const parsedLocationId = locationParam ? Number(locationParam) : NaN;
      const parsedContactId = contactParam ? Number(contactParam) : NaN;

      if (
        Number.isNaN(parsedServiceId) ||
        Number.isNaN(parsedLocationId) ||
        Number.isNaN(parsedContactId)
      ) {
        this.errorMessage.set('Invalid identifiers for contact details.');
        this.loading.set(false);
        return;
      }

      this.serviceId.set(parsedServiceId);
      this.locationId.set(parsedLocationId);
      this.contactId.set(parsedContactId);
      this.uiState.setServiceId(parsedServiceId);
      this.uiState.setLocationId(parsedLocationId);
      this.fetchContact(parsedServiceId, parsedLocationId, parsedContactId);
    });

    this.route.queryParamMap.subscribe((queryParams) => {
      const target =
        (queryParams.get('returnTo') ?? '').trim().toLowerCase() === 'company'
          ? 'company'
          : 'contacts';
      this.returnTo.set(target);
    });
  }

  protected goBack(): void {
    const serviceId = this.serviceId();
    const locationId = this.locationId();
    if (!serviceId || !locationId) {
      this.router.navigate(['/']);
      return;
    }
    this.uiState.setServiceId(serviceId);
    this.uiState.setLocationId(locationId);
    if (this.returnTo() === 'company') {
      this.router.navigate(['/companies', serviceId, locationId]);
    } else {
      this.router.navigate(['/contacts', serviceId, locationId]);
    }
  }

  protected renderList(value: string[] | null): string[] {
    return Array.isArray(value) ? value : [];
  }

  protected renderSocialMedia(
    value: Record<string, unknown> | null
  ): Array<{ key: string; value: string }> {
    if (!value || typeof value !== 'object') {
      return [];
    }
    return Object.entries(value).map(([key, val]) => ({
      key,
      value: Array.isArray(val)
        ? val.map((item) => String(item)).join(', ')
        : typeof val === 'string'
        ? val
        : JSON.stringify(val)
    }));
  }

  private fetchContact(
    serviceId: number,
    locationId: number,
    contactId: number
  ): void {
    this.loading.set(true);
    this.errorMessage.set('');
    this.statusMessage.set('Loading contact details...');
    this.linkedinLoading.set(false);
    this.linkedinAvailable.set(false);

    this.collectDataService
      .getCompanyContactDetails(serviceId, locationId)
      .pipe(
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (response: CompanyContactResponse) => {
          const matched =
            response.data.find((item) => item.id === contactId) ?? null;

          if (!matched) {
            this.errorMessage.set(
              'Contact details were not found. They may have been removed.'
            );
            this.record.set(null);
            this.statusMessage.set('');
            return;
          }

          this.record.set(matched);
          this.statusMessage.set('Contact details loaded successfully.');
          this.serviceDetails.set(null);
          this.serviceError.set('');
          this.linkedinAvailable.set(false);
          this.syncLinkedInAvailability(matched.id);
          this.checkStoredLinkedInProfiles(
            serviceId,
            locationId,
            matched.id
          );
        },
        error: (error) => {
          this.errorMessage.set(this.extractErrorMessage(error));
          this.statusMessage.set('');
          this.record.set(null);
        }
      });
  }

  protected viewServiceDetails(): void {
    const record = this.record();
    const serviceId = this.serviceId();
    const locationId = this.locationId();
    if (!record || !serviceId || !locationId) {
      this.serviceError.set(
        'Missing identifiers to request service and product details.'
      );
      return;
    }

    const companyName = record.company_name?.trim() || null;
    const rawWebsite = record.website?.trim() || null;
    const companyWebsite =
      rawWebsite && rawWebsite.toLowerCase() !== 'n/a' ? rawWebsite : null;

    this.serviceError.set('');
    this.serviceDetails.set(null);
    this.serviceLoading.set(true);

    this.collectDataService
      .getCompanyServiceDetails(
        serviceId,
        locationId,
        companyName,
        companyWebsite
      )
      .pipe(
        finalize(() => {
          this.serviceLoading.set(false);
        })
      )
      .subscribe({
        next: (response) => {
          this.serviceDetails.set(response);
          if (!response.service_details?.length) {
            this.serviceError.set(
              'No service or product information was detected on the company website.'
            );
          }
        },
        error: (error) => {
          this.serviceError.set(this.extractErrorMessage(error));
        }
      });
  }

  private extractErrorMessage(error: unknown): string {
    if (!error || typeof error !== 'object') {
      return 'An unexpected error occurred.';
    }
    const maybeResponse = error as {
      error?: { detail?: string };
      message?: string;
    };
    return (
      maybeResponse?.error?.detail ||
      maybeResponse?.message ||
      'An unexpected error occurred.'
    );
  }

  protected fetchLinkedInDetails(): void {
    const serviceId = this.serviceId();
    const locationId = this.locationId();
    const contactId = this.contactId();
    const companyName = this.record()?.company_name?.trim() || null;
    const searchQuery = companyName
      ? `site:linkedin.com/in "${companyName}" current`
      : null;

    if (this.linkedinLoading()) {
      return;
    }

    if (serviceId == null || locationId == null || contactId == null) {
      this.errorMessage.set(
        'Missing identifiers to fetch LinkedIn profile details.'
      );
      return;
    }

    this.errorMessage.set('');
    this.statusMessage.set('');

    if (this.linkedinAvailable()) {
      this.router.navigate(
        ['/contacts', serviceId, locationId, contactId, 'linkedin'],
        { state: { linkedinReady: true, contactId } }
      );
      return;
    }

    this.statusMessage.set('Crawling LinkedIn data, please wait...');
    this.linkedinLoading.set(true);

    this.collectDataService
      .refreshLinkedInProfileDetails(
        serviceId,
        contactId,
        locationId,
        searchQuery
      )
      .subscribe({
        next: async () => {
          this.statusMessage.set('Redirecting to LinkedIn details...');
          let navigationSucceeded = false;
          try {
            this.markLinkedInAvailable(contactId);
            navigationSucceeded = await this.router.navigate(
              ['/contacts', serviceId, locationId, contactId, 'linkedin'],
              { queryParams: { refresh: 1 } }
            );
            if (!navigationSucceeded) {
              this.errorMessage.set(
                'Unable to open the LinkedIn details page.'
              );
            }
          } catch {
            this.errorMessage.set('Unable to open the LinkedIn details page.');
          } finally {
            this.linkedinLoading.set(false);
            this.statusMessage.set('');
          }
        },
        error: (error) => {
          this.linkedinLoading.set(false);
          const message = this.extractErrorMessage(error);
          this.errorMessage.set(message);
          this.statusMessage.set('');
        }
      });
  }

  private syncLinkedInAvailability(contactId: number): void {
    if (typeof window === 'undefined') {
      this.linkedinAvailable.set(false);
      return;
    }
    const state = window.history.state as {
      linkedinReady?: boolean;
      contactId?: number;
    };
    const available = Boolean(
      state?.linkedinReady && state?.contactId === contactId
    );
    this.linkedinAvailable.set(available);
  }

  private checkStoredLinkedInProfiles(
    serviceId: number,
    locationId: number,
    contactId: number
  ): void {
    this.collectDataService
      .getLinkedInProfileDetails(serviceId, contactId, locationId)
      .subscribe({
        next: (response) => {
          const status = (response.status || '').toLowerCase();
          const total =
            typeof response.total_profiles === 'number'
              ? response.total_profiles
              : Array.isArray(response.profiles)
              ? response.profiles.length
              : 0;
          if (status === 'success' && total > 0) {
            this.markLinkedInAvailable(contactId);
          }
        },
        error: () => {
          // Ignore failures when probing stored LinkedIn data
        }
      });
  }

  private markLinkedInAvailable(contactId: number): void {
    this.linkedinAvailable.set(true);
    if (typeof window === 'undefined') {
      return;
    }
    const currentState =
      (window.history.state as Record<string, unknown>) ?? {};
    try {
      window.history.replaceState(
        { ...currentState, linkedinReady: true, contactId },
        '',
        window.location.href
      );
    } catch {
      // Ignore history state errors (e.g., cross-origin restrictions)
    }
  }
}




